// Cloudflare Turnstile, behind a config flag (T57 WP5).
//
// Entirely inert until `VITE_TURNSTILE_SITE_KEY` is set at build time: no script
// is loaded, no widget rendered, and every caller gets `null` for the token. So
// this can ship before John creates the widget, and turning it on is a rebuild
// with one env var rather than a code change.
//
// Two places it matters, and they are protected differently:
//
//  1. `submit-provider-application` verifies the token itself, but only when
//     TURNSTILE_SECRET is set on the function — the existing `verifyTurnstile`
//     returns true when unconfigured. Both halves are independently flagged, so
//     a half-configured deployment degrades to "no captcha" rather than to
//     "nobody can apply".
//
//  2. The magic-link requests (`signInWithOtp`). This is the one that actually
//     needs it: an unauthenticated caller can ask GoTrue to mail anyone. Supabase
//     verifies `options.captchaToken` server-side when captcha is enabled in the
//     project's Auth settings — so enabling it is John's switch, not ours, and
//     sending a token when it is off is harmless.

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void;
                                    'error-callback'?: () => void; theme?: string;
                                    size?: string }) => string;
  reset: (id?: string) => void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

export function turnstileEnabled(): boolean {
  return typeof SITE_KEY === 'string' && SITE_KEY.trim().length > 0;
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_URL;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

/**
 * Mount a widget into `container` and resolve with its token.
 *
 * Resolves `null` when Turnstile is not configured, when the script cannot load,
 * or when the challenge errors — never rejects. A captcha that is down must not
 * become a wall in front of a provider trying to claim their business; the
 * server-side caps (WP5) are the control that always holds, and the captcha is
 * the one that reduces noise when it is available.
 */
export async function getTurnstileToken(container: HTMLElement): Promise<string | null> {
  if (!turnstileEnabled()) return null;
  try {
    await loadScript();
    const api = window.turnstile;
    if (!api) return null;
    return await new Promise<string | null>((resolve) => {
      const timer = window.setTimeout(() => resolve(null), 15_000);
      api.render(container, {
        sitekey: SITE_KEY as string,
        size: 'flexible',
        callback: (token: string) => { window.clearTimeout(timer); resolve(token); },
        'error-callback': () => { window.clearTimeout(timer); resolve(null); },
      });
    });
  } catch {
    return null;
  }
}

/**
 * Options for `signInWithOtp`. Empty when Turnstile is off, so call sites read
 * the same either way: `{ ...base, ...captchaOptions(token) }`.
 */
export function captchaOptions(token: string | null): { captchaToken?: string } {
  return token ? { captchaToken: token } : {};
}
